import React, { useState, useEffect } from 'react';
import theme from './ExternalAgreementDetails.module.scss';
import { ExternalAgreement, ExternalAgreementStatus, BatchedChanges } from '@/types';
import AttachmentList from '@/components/AttachmentList';
import { Trans } from '@lingui/react/macro';
import { PriceDisplay, FeeSetConnectionInfoDisplay } from '@/seller/components/fees';
import { UserIcon } from '@/components/Icons';
import { getOpenExternalAgreementFileUrl, updateExternalAgreement } from '@/services/external-agreements';
import { Button } from '@/components/Buttons';
import ExternalAgreementRejectDialog from '../ExternalAgreementRejectDialog';
import { faCheckCircle, faTimesCircle, faEdit } from '@fortawesome/pro-light-svg-icons';
import ExternalAgreementApproveDialog from '../ExternalAgreementApproveDialog';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { ProjectCurrencyDisplay } from '@/seller/components/currency';
import { sumBy } from 'lodash-es';
import ExternalAgreementEditPriceDialog from '../ExternalAgreementEdit/ExternalAgreementEditPriceDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExternalAgreementEditParticipantsDialog from '../ExternalAgreementEdit/ExternalAgreementEditParticipantsDialog';
import { useThreadUnreadMessageCount } from '@/utils/hooks';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import HistoryLogTable from '@/seller/components/HistoryLog/HistoryLogTable';
import { CommentThread } from '@ygtechnologies/bygr-frontend-chat';
import { CommentThreadWrapper } from '@/components/Comments/CommentThreadWrapper';
import { Tabs } from '@ygtechnologies/rubus-ui';

type ExternalAgreementTabId = 'overview' | 'chat' | 'log';

interface ExternalAgreementDetailsProps {
    externalAgreement: ExternalAgreement;
    onChange: (changes: BatchedChanges<ExternalAgreement>) => void;
}

const ExternalAgreementDetails: React.FC<ExternalAgreementDetailsProps> = ({ externalAgreement, onChange }) => {
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [showApproveDialog, setShowApproveDialog] = useState(false);
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [isRetracted, setIsRetracted] = useState(false);
    const [activeTab, setActiveTab] = useState<ExternalAgreementTabId>('overview');
    const [showEditPrice, setShowEditPrice] = useState(false);
    const [showEditMessage, setShowEditMessage] = useState(false);
    const unreadMessages = useThreadUnreadMessageCount(externalAgreement.commentThreadId);

    useEffect(() => {
        setIsRetracted(externalAgreement.lines.filter((x) => x.priceRedacted).length > 0);
    }, [externalAgreement]);

    const handleRejectDialogAccept = (reason: string) => {
        onChange(
            BatchedChanges.single(externalAgreement.id, { status: ExternalAgreementStatus.Rejected, rejectReason: reason })
        );
    };
    const handleApproveDialogAccept = () => {
        onChange(BatchedChanges.single(externalAgreement.id, { status: ExternalAgreementStatus.Approved }));
    };

    const saveFiles = async (newFiles) => {
        const ea = await updateExternalAgreement(externalAgreement.id, {
            files: newFiles,
        });
        onChange(BatchedChanges.single(externalAgreement.id, ea));
    };

    const renderButtons = () => {
        if (!externalAgreement.permissions.canApprove || externalAgreement.status !== ExternalAgreementStatus.Pending) {
            return null;
        }

        return (
            <div className={theme.buttons}>
                <Button color="success" icon={faCheckCircle} onClick={(e) => setShowApproveDialog(true)}>
                    <Trans>Approve</Trans>
                </Button>
                <Button color="danger" icon={faTimesCircle} onClick={(e) => setShowRejectDialog(true)}>
                    <Trans>Reject</Trans>
                </Button>

                <ExternalAgreementRejectDialog
                    show={showRejectDialog}
                    externalAgreement={externalAgreement}
                    onClose={() => setShowRejectDialog(false)}
                    onReject={handleRejectDialogAccept}
                />

                <ExternalAgreementApproveDialog
                    show={showApproveDialog}
                    externalAgreement={externalAgreement}
                    projectId={projectId}
                    onClose={() => setShowApproveDialog(false)}
                    onApprove={handleApproveDialogAccept}
                />
            </div>
        );
    };

    const editable = externalAgreement.permissions.canEdit && externalAgreement.status === ExternalAgreementStatus.Pending;

    return (
        <>
            <Tabs
                className={theme.tabs}
                activeId={activeTab}
                onChange={(id) => setActiveTab(id as ExternalAgreementTabId)}
                size="sm"
                variant="underline">
                <Tabs.Tab id="overview" label={<Trans>Overview</Trans>} />
                <Tabs.Tab id="chat" label={<Trans>Internal messages</Trans>} badge={unreadMessages} />
                <Tabs.Tab id="log" label={<Trans>History log</Trans>} />
            </Tabs>
            {activeTab === 'overview' && (
                <>
                    <div className={theme.row}>
                        <div className={theme.section}>
                            <h5>
                                <Trans>Price breakdown</Trans>
                                {editable && (
                                    <span className={theme.editButton}>
                                        <FontAwesomeIcon icon={faEdit} onClick={() => setShowEditPrice(true)} />
                                    </span>
                                )}
                            </h5>
                            <table className="table style-3">
                                <thead>
                                    <tr>
                                        <th>
                                            <Trans>Description</Trans>
                                        </th>
                                        <th>
                                            <Trans>Fees</Trans>
                                        </th>
                                        <th>
                                            <Trans>Category</Trans>
                                        </th>
                                        <th className="text-right">
                                            <Trans>Line total</Trans>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {externalAgreement.lines.map((line) => (
                                        <tr key={line.id}>
                                            <td>{line.description}</td>
                                            <td>
                                                {line.feeSetInfo ? (
                                                    <FeeSetConnectionInfoDisplay value={line.feeSetInfo} />
                                                ) : (
                                                    <Trans>Redacted</Trans>
                                                )}
                                            </td>
                                            <td>
                                                <ItemCategoryDisplay value={line.itemCategoryId} />
                                            </td>
                                            <td className="text-right">
                                                <PriceDisplay
                                                    value={line.priceCalculation}
                                                    redacted={line.priceRedacted}
                                                    inline={true}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colSpan={3}>
                                            <Trans>Total</Trans>
                                        </th>
                                        <th className="text-right">
                                            <span>
                                                <ProjectCurrencyDisplay
                                                    value={sumBy(
                                                        externalAgreement.lines
                                                            .map((x) => x.priceCalculation)
                                                            .filter((x) => !!x),
                                                        'finalPrice'
                                                    )}
                                                />
                                            </span>
                                            {!isRetracted && (
                                                <small>
                                                    &nbsp;(
                                                    <ProjectCurrencyDisplay
                                                        value={sumBy(
                                                            externalAgreement.lines
                                                                .map((x) => x.priceCalculation)
                                                                .filter((x) => !!x),
                                                            'basePrice'
                                                        )}
                                                    />
                                                    )
                                                </small>
                                            )}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <div className={theme.section}>
                            <h5>
                                <Trans>Suggested participants</Trans>
                                {editable && (
                                    <span className={theme.editButton}>
                                        <FontAwesomeIcon icon={faEdit} onClick={() => setShowEditMessage(true)} />
                                    </span>
                                )}
                            </h5>
                            <ul className={theme.participants}>
                                {externalAgreement.participants.map((participant) => (
                                    <li key={`${participant.userId}_${participant.profileId}`}>
                                        <UserIcon sizeInPx={36} className={theme.avatar} userId={participant.userId} />
                                        <div>
                                            {participant.userName}
                                            <small>{participant.profileName}</small>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            {externalAgreement.participantsNote && (
                                <>
                                    <h5 className={theme.messageTitle}>
                                        <Trans>Note for participants</Trans>
                                        {editable && (
                                            <span className={theme.editButton}>
                                                <FontAwesomeIcon icon={faEdit} onClick={() => setShowEditMessage(true)} />
                                            </span>
                                        )}
                                    </h5>
                                    <div className={theme.participantNote}>{externalAgreement.participantsNote}</div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={theme.bottom}>
                        <div className={theme.bottom}>
                            <h5>
                                <Trans>Attachments</Trans>
                            </h5>
                            <div className={theme.attachmentList}>
                                <AttachmentList
                                    showVisibilityFlags
                                    value={externalAgreement.files}
                                    canEdit={editable}
                                    canAdd={editable}
                                    onChange={(newFiles) => saveFiles(newFiles)}
                                    urlGenerator={(att) => getOpenExternalAgreementFileUrl(att.id)}
                                    ownerProfileId={externalAgreement.createdByProfile.id}
                                    itemsPerRow={4}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}
            {activeTab === 'chat' && (
                <div className={theme.commentsContainer}>
                    <CommentThreadWrapper threadId={externalAgreement.commentThreadId} variant="flat" />
                </div>
            )}
            {activeTab === 'log' && (
                <HistoryLogTable
                    hiddenColumns={['objectName']}
                    filter={{
                        projectId: externalAgreement.projectId,
                        objectType: 'ExternalAgreement',
                        objectIds: [externalAgreement.id],
                    }}
                />
            )}
            {renderButtons()}
            <ExternalAgreementEditPriceDialog
                externalAgreement={externalAgreement}
                show={showEditPrice}
                onClose={() => setShowEditPrice(false)}
                onSave={(externalAgreement: ExternalAgreement) => {
                    setShowEditPrice(false);
                    onChange(BatchedChanges.single(externalAgreement.id, externalAgreement));
                }}
            />
            <ExternalAgreementEditParticipantsDialog
                externalAgreement={externalAgreement}
                show={showEditMessage}
                onClose={() => setShowEditMessage(false)}
                onSave={(externalAgreement: ExternalAgreement) => {
                    setShowEditMessage(false);
                    onChange(BatchedChanges.single(externalAgreement.id, externalAgreement));
                }}
            />
        </>
    );
};

export default ExternalAgreementDetails;
